/**
 * Stage Editor — shared module-level helpers (no scene state).
 *
 * Pure helpers + presentation constants extracted from
 * StageEditorScene.js so the scene file stays focused on the class.
 * Imported back by StageEditorScene.js and editor chrome modules.
 */

/**
 * Presentation metadata for each editable game, used by the in-editor
 * launcher (home screen) and the toolbar brand. The set of editable
 * games is whatever `SCHEMA_REGISTRY` exposes; this only adds the
 * human-facing title / blurb / icon.
 */
const GAME_META = {
    trainsim: {
        title: 'Train Simulator',
        blurb: 'Lay tracks, place trains, schedule signal lamps and switches on a fixed grid. Every train must reach its goal.',
        icon: 'train',
        accent: 'sky',
    },
    platformer: {
        title: 'Platformer',
        blurb: 'Paint terrain, drop enemies, coins and the goal flag across a scrolling Mario-style level, then test-play instantly.',
        icon: 'sports_esports',
        accent: 'emerald',
    },
};

/** Human-readable cardinal/diagonal direction labels keyed by the
 *  same 0..7 indices used throughout the trainsim graph helpers. */
const DIR_LABELS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/**
 * Walk from `from` to `to` returning the cells visited in order
 * (EXCLUDING `from`, INCLUDING `to`). Used to fill in cells the
 * pointer-move skipped during a fast drag-erase sweep so the erase
 * tool produces a continuous swath instead of dotted gaps.
 *
 * Behaviour:
 *   - pure cardinal jump (one axis is zero)  → straight cells
 *   - pure diagonal jump (|dc| === |dr|)      → diagonal cells
 *   - mixed jump                              → cardinal-first L
 */
function interpolateCellPath(from, to) {
    const dc = to.c - from.c;
    const dr = to.r - from.r;
    if (dc === 0 && dr === 0) return [];
    const absDc = Math.abs(dc), absDr = Math.abs(dr);
    const sx = Math.sign(dc), sy = Math.sign(dr);

    const out = [];
    let c = from.c, r = from.r;

    if (dc === 0 || dr === 0) {
        while (c !== to.c || r !== to.r) {
            c += sx; r += sy;
            out.push({ c, r });
        }
        return out;
    }
    if (absDc === absDr) {
        while (c !== to.c) {
            c += sx; r += sy;
            out.push({ c, r });
        }
        return out;
    }
    if (absDc >= absDr) {
        while (c !== to.c) { c += sx; out.push({ c, r }); }
        while (r !== to.r) { r += sy; out.push({ c, r }); }
    } else {
        while (r !== to.r) { r += sy; out.push({ c, r }); }
        while (c !== to.c) { c += sx; out.push({ c, r }); }
    }
    return out;
}

/**
 * Cells touched by the STRAIGHT segment from `from` to `to`, in order,
 * EXCLUDING `from` and INCLUDING `to` (Bresenham).
 *
 * This is what freeform brush / erase SWEEPS use — unlike
 * `interpolateCellPath` (which draws clean L / diagonal shapes for
 * Shift+click track-laying), this traces only the cells the cursor
 * actually swept across, so a drag paints exactly the cells under the
 * pointer's path and never takes an L-shaped detour through cells the
 * mouse never visited.
 *
 * `maxLen` caps how many cells a single gap may fill. Normal pointer-
 * move sampling keeps consecutive cells adjacent, so this only ever
 * trips on a pathological jump (e.g. a stray sample mid-rebuild); in
 * that case we bail to an empty path so the stroke can't smear across
 * the whole board.
 */
function traceCellLine(from, to, maxLen = 64) {
    let x0 = from.c, y0 = from.r;
    const x1 = to.c, y1 = to.r;
    if (x0 === x1 && y0 === y1) return [];
    const dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
    if (dx + dy > maxLen) return [];
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    const out = [];
    let guard = dx + dy + 2;
    while (guard-- > 0) {
        const e2 = 2 * err;
        if (e2 > -dy) { err -= dy; x0 += sx; }
        if (e2 < dx)  { err += dx; y0 += sy; }
        out.push({ c: x0, r: y0 });
        if (x0 === x1 && y0 === y1) break;
    }
    return out;
}

/**
 * Inline SVG cursors keyed by active tool. The colour communicates
 * the tool at a glance (matching the toolbar palette) and the shape
 * communicates the action — a brush tip for paint, an X for erase,
 * an arrow for select.
 *
 * The values are full `cursor:` CSS properties (data-URI SVG + hotspot
 * offset + a default fallback) so `_updateBoardCursor` can swap them
 * directly into the hit-area cursor.
 */
const TOOL_CURSORS = {
    paint:   buildCursor('#22c55e', 'brush'),
    erase:   buildCursor('#ef4444', 'x'),
    select:  buildCursor('#3b82f6', 'arrow'),
    fill:    buildCursor('#f59e0b', 'bucket'),
    eyedrop: buildCursor('#a855f7', 'dropper'),
};

/** Tile / hover stroke colour per tool — matches the cursor colour. */
const TOOL_COLORS = {
    paint:   0x22c55e,
    erase:   0xef4444,
    select:  0x3b82f6,
    fill:    0xf59e0b,
    eyedrop: 0xa855f7,
};

/**
 * Inline SVG icon strings for toolbar buttons. All paths sit in a
 * 24×24 viewBox, use `currentColor` for the stroke / fill so they
 * inherit the button's text colour, and are designed to read clearly
 * at 18×18 px (the toolbar size). Returning raw SVG strings rather
 * than DOM nodes keeps the toolbar build cheap — we splat them
 * straight into the button via `innerHTML`.
 */
const TOOL_ICONS = {
    paint: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M3 21 L9 15 L12 18 L18 12 L21 15 L15 21 Z' fill='currentColor' fill-opacity='0.25'/>
        <path d='M14 4 L20 10 L13 17 L7 11 Z' />
        <path d='M14 4 L17 1 L23 7 L20 10' />
    </svg>`,
    erase: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M16 3 L21 8 L10 19 L3 19 L3 13 Z' />
        <path d='M9 9 L15 15' />
    </svg>`,
    select: `<svg viewBox='0 0 24 24' fill='currentColor' stroke='currentColor' stroke-width='1.5' stroke-linejoin='round'>
        <path d='M4 3 L4 20 L9 15 L12 22 L15 21 L12 14 L19 14 Z' />
    </svg>`,
    fill: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M11 2 L11 6' />
        <path d='M5 12 L11 6 L19 14 L12 21 A2 2 0 0 1 9 21 L5 17 A2 2 0 0 1 5 12 Z' fill='currentColor' fill-opacity='0.2'/>
        <path d='M5 14 H18' />
        <path d='M21 17 C21 19 20 20 20 20 A1.6 1.6 0 0 1 18.4 18.4 C18.6 17.4 21 15.5 21 17 Z' fill='currentColor'/>
    </svg>`,
    eyedrop: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M14 7 L17 10 L8.5 18.5 A2.1 2.1 0 0 1 5.5 15.5 Z' fill='currentColor' fill-opacity='0.2'/>
        <path d='M13 6 L18 11' />
        <path d='M15 4 A2.5 2.5 0 0 1 20 9 L18 11 L13 6 Z' fill='currentColor'/>
        <path d='M5.5 15.5 L4 20 L8.5 18.5' />
    </svg>`,
    undo: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M9 14 L4 9 L9 4' />
        <path d='M4 9 H14 A6 6 0 0 1 14 21 H11' />
    </svg>`,
    redo: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M15 14 L20 9 L15 4' />
        <path d='M20 9 H10 A6 6 0 0 0 10 21 H13' />
    </svg>`,
    rotate: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M3 12 A9 9 0 1 1 6 18.5' />
        <path d='M3 18 L3 13 L8 13' />
    </svg>`,
    panels: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <rect x='3' y='4' width='18' height='16' rx='2' />
        <line x1='9' y1='4' x2='9' y2='20' />
        <line x1='15' y1='4' x2='15' y2='20' />
    </svg>`,
    play: `<svg viewBox='0 0 24 24' fill='currentColor' stroke='currentColor' stroke-width='1.5' stroke-linejoin='round'>
        <path d='M6 4 L20 12 L6 20 Z' />
    </svg>`,
    save: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M5 3 H16 L21 8 V21 H5 Z' />
        <rect x='8' y='14' width='8' height='6' />
        <rect x='8' y='3' width='7' height='5' />
    </svg>`,
    load: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M3 7 L3 19 H21 V9 H12 L10 7 Z' />
    </svg>`,
    clear: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M4 7 H20' />
        <path d='M9 7 V4 H15 V7' />
        <path d='M6 7 L7 21 H17 L18 7' />
        <path d='M10 11 V17' />
        <path d='M14 11 V17' />
    </svg>`,
    back: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M19 12 H5' />
        <path d='M11 6 L5 12 L11 18' />
    </svg>`,
    test: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M9 3 H15' />
        <path d='M10 3 V9 L4 19 A2 2 0 0 0 6 22 H18 A2 2 0 0 0 20 19 L14 9 V3' />
        <path d='M7.5 14 H16.5' />
        <circle cx='10.5' cy='17' r='0.9' fill='currentColor' />
        <circle cx='14' cy='18.5' r='0.7' fill='currentColor' />
    </svg>`,
    widen: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <rect x='3' y='6' width='18' height='12' rx='1.5' />
        <path d='M7 9 L4 12 L7 15' />
        <path d='M17 9 L20 12 L17 15' />
    </svg>`,
    narrow: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <rect x='3' y='6' width='18' height='12' rx='1.5' />
        <path d='M5 9 L8 12 L5 15' />
        <path d='M19 9 L16 12 L19 15' />
    </svg>`,
    width: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M3 12 H21' />
        <path d='M6 9 L3 12 L6 15' />
        <path d='M18 9 L21 12 L18 15' />
    </svg>`,
    assets: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <rect x='3' y='3' width='18' height='18' rx='2' />
        <circle cx='8.5' cy='8.5' r='1.5' />
        <path d='M21 15 L16 10 L5 21' />
    </svg>`,
    home: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M4 11 L12 4 L20 11' />
        <path d='M6 10 V20 H18 V10' />
        <path d='M10 20 V14 H14 V20' />
    </svg>`,
    more: `<svg viewBox='0 0 24 24' fill='currentColor' stroke='none'>
        <circle cx='5' cy='12' r='2' />
        <circle cx='12' cy='12' r='2' />
        <circle cx='19' cy='12' r='2' />
    </svg>`,
    help: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <circle cx='12' cy='12' r='9' />
        <path d='M9.2 9 A2.9 2.9 0 1 1 12 12.2 V14' />
        <circle cx='12' cy='17.4' r='0.9' fill='currentColor' stroke='none' />
    </svg>`,
    fit: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <path d='M4 9 V4 H9' />
        <path d='M20 9 V4 H15' />
        <path d='M4 15 V20 H9' />
        <path d='M20 15 V20 H15' />
    </svg>`,
    zoomIn: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <circle cx='11' cy='11' r='7' />
        <path d='M21 21 L16 16' />
        <path d='M11 8 V14 M8 11 H14' />
    </svg>`,
    zoomOut: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
        <circle cx='11' cy='11' r='7' />
        <path d='M21 21 L16 16' />
        <path d='M8 11 H14' />
    </svg>`,
};

function buildCursor(color, shape) {
    const svg = (() => {
        if (shape === 'brush') {
            // Brush tip: filled circle + tail stroke, hot-spotted at the
            // bottom-left so the visible "tip" sits on the target cell.
            return `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
                <path d='M3 25 L13 15' stroke='${color}' stroke-width='3' stroke-linecap='round'/>
                <circle cx='15' cy='13' r='6' fill='${color}' stroke='white' stroke-width='2'/>
                <circle cx='3' cy='25' r='2' fill='white'/>
            </svg>`;
        }
        if (shape === 'x') {
            // Crossbones-style X, hot-spotted at centre.
            return `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
                <line x1='6' y1='6'  x2='22' y2='22' stroke='${color}' stroke-width='4' stroke-linecap='round'/>
                <line x1='22' y1='6' x2='6'  y2='22' stroke='${color}' stroke-width='4' stroke-linecap='round'/>
                <line x1='6' y1='6'  x2='22' y2='22' stroke='white' stroke-width='1.5' stroke-linecap='round'/>
                <line x1='22' y1='6' x2='6'  y2='22' stroke='white' stroke-width='1.5' stroke-linecap='round'/>
            </svg>`;
        }
        if (shape === 'bucket') {
            // Paint bucket: tilted pot + pour drip, hot-spotted at the
            // drip tip (bottom-left) so the fill origin is unambiguous.
            return `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
                <path d='M9 6 L21 14 L14 23 L6 17 Z' fill='${color}' stroke='white' stroke-width='2' stroke-linejoin='round'/>
                <path d='M13 3 L13 9' stroke='white' stroke-width='2' stroke-linecap='round'/>
                <path d='M4 21 C4 23.2 5 25 6 25 C7 25 8 23.2 8 21 C8 19.5 6 17.5 6 17.5 C6 17.5 4 19.5 4 21 Z' fill='${color}' stroke='white' stroke-width='1.5'/>
            </svg>`;
        }
        if (shape === 'dropper') {
            // Pipette, hot-spotted at the tip (bottom-left).
            return `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
                <path d='M16 8 L20 12 L9 23 L4 24 L5 19 Z' fill='${color}' stroke='white' stroke-width='2' stroke-linejoin='round'/>
                <path d='M17 5 A3 3 0 0 1 23 11 L20 14 L14 8 Z' fill='${color}' stroke='white' stroke-width='1.5'/>
            </svg>`;
        }
        // arrow
        return `<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
            <path d='M5 4 L5 22 L10 17 L13 24 L16 23 L13 16 L20 16 Z' fill='${color}' stroke='white' stroke-width='1.5' stroke-linejoin='round'/>
        </svg>`;
    })();
    const encoded = encodeURIComponent(svg.replace(/\s+/g, ' ').trim());
    const hotspot =
        shape === 'x' ? '14 14'
        : shape === 'brush' ? '3 25'
        : shape === 'bucket' ? '6 24'
        : shape === 'dropper' ? '4 24'
        : '5 4';
    return `url("data:image/svg+xml;utf8,${encoded}") ${hotspot}, default`;
}

/**
 * Shared button factory for the editor chrome (top bar, tool rail,
 * status bar). Builds a `.se-btn`-based element with an optional SVG
 * icon (from TOOL_ICONS), text label and keyboard-hint chip. Kept at
 * module scope so every chrome mount can reuse one consistent button.
 */
function makeSeButton(opts = {}) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `se-btn ${opts.cls || ''}`.trim();
    if (opts.title) b.title = opts.title;
    if (opts.iconKey) {
        const icon = document.createElement('span');
        icon.className = 'se-btn-icon';
        icon.innerHTML = TOOL_ICONS[opts.iconKey] || '';
        b.appendChild(icon);
    }
    if (opts.label) {
        const lbl = document.createElement('span');
        lbl.className = 'se-btn-label';
        lbl.textContent = opts.label;
        b.appendChild(lbl);
    }
    if (opts.kbd) {
        const kbd = document.createElement('span');
        kbd.className = 'se-btn-kbd';
        kbd.textContent = opts.kbd;
        b.appendChild(kbd);
    }
    if (opts.onClick) b.onclick = opts.onClick;
    return b;
}


// ── helpers ────────────────────────────────────────

function tileAllowedOnLayer(tdef, layer) {
    if (Array.isArray(tdef.allowedLayerIds) && tdef.allowedLayerIds.length) {
        return tdef.allowedLayerIds.includes(layer.id);
    }
    if (Array.isArray(tdef.allowedLayerKinds) && tdef.allowedLayerKinds.length) {
        return tdef.allowedLayerKinds.includes(layer.kind);
    }
    return true;
}

function entityAllowedOnLayer(edef, layer) {
    if (Array.isArray(edef.allowedLayerIds) && edef.allowedLayerIds.length) {
        return edef.allowedLayerIds.includes(layer.id);
    }
    if (Array.isArray(edef.allowedLayerKinds) && edef.allowedLayerKinds.length) {
        return edef.allowedLayerKinds.includes(layer.kind);
    }
    return true;
}


export {
    GAME_META, DIR_LABELS,
    interpolateCellPath, traceCellLine,
    TOOL_CURSORS, TOOL_COLORS, TOOL_ICONS,
    buildCursor, makeSeButton,
    tileAllowedOnLayer, entityAllowedOnLayer,
};
